import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';

import ContactNotesDialogListItem from '../ContactNotesDialogListItem';
import ConfirmationModal from '../ConfirmationModal';

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";

import useTicketNotes from '../../hooks/useTicketNotes';

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        marginTop: theme.spacing(2),
    },
    title: {
        marginBottom: theme.spacing(1),
        fontSize: '0.875rem',
        fontWeight: 500,
    },
    inputContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    textField: {
        flex: 1,
        '& .MuiInputBase-root': {
            fontSize: '0.875rem',
        },
    },
    list: {
        width: '100%',
        maxHeight: '200px',
        backgroundColor: theme.palette.background.paper,
        overflow: 'auto',
        marginTop: theme.spacing(1),
        padding: 0,
    },
    addButton: {
        marginTop: theme.spacing(0.5),
        padding: theme.spacing(1),
    },
}));

export function ContactNotesStandalone ({ contactId }) {
    const classes = useStyles();
    const [noteText, setNoteText] = useState("");
    const [loading, setLoading] = useState(false);
    const [showOnDeleteDialog, setShowOnDeleteDialog] = useState(false);
    const [selectedNote, setSelectedNote] = useState({});
    const [notes, setNotes] = useState([]);
    const { saveNote, deleteNote, listNotesByContactId } = useTicketNotes();
    const isMountedRef = React.useRef(true); // ✅ CORRECCIÓN: useRef para rastrear si el componente está montado

    useEffect(() => {
        isMountedRef.current = true; // ✅ Marcar como montado
        
        const loadData = async () => {
            if (contactId && isMountedRef.current) {
                await loadNotes();
            }
        };
        
        loadData();
        
        // ✅ CORRECCIÓN: Cleanup function
        return () => {
            isMountedRef.current = false; // ✅ Marcar como desmontado
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactId]);

    const handleSave = async () => {
        if (!contactId) {
            toast.error("Debe seleccionar un contacto primero");
            return;
        }

        if (!noteText || noteText.trim().length < 3) {
            toast.error("La observación debe tener al menos 3 caracteres");
            return;
        }

        setLoading(true);
        try {
            // ✅ Crear nota sin ticket asociado (null o 0 dependiendo de lo que acepte el backend)
            await saveNote({
                note: noteText.trim(),
                contactId,
                ticketId: null // ✅ Usar null en lugar de 0
            });
            await loadNotes();
            setNoteText("");
            toast.success(i18n.t("contactModal.notes.success") || 'Observación agregada exitosamente');
        } catch (e) {
            console.error("Error guardando nota:", e);
            toast.error(e?.response?.data?.message || e?.message || 'Error al agregar observación');
        }
        setLoading(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            handleSave();
        }
    };

    const handleOpenDialogDelete = (item) => {
        setSelectedNote(item);
        setShowOnDeleteDialog(true);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteNote(selectedNote.id);
            await loadNotes();
            setSelectedNote({});
            setShowOnDeleteDialog(false);
            toast.success(i18n.t("contactModal.notes.deleted") || 'Observación eliminada exitosamente');
        } catch (e) {
            console.error("Error eliminando nota:", e);
            toast.error(e?.response?.data?.message || e?.message || 'Error al eliminar observación');
        }
        setLoading(false);
    };

    const loadNotes = async () => {
        if (!contactId) return;
        
        setLoading(true);
        try {
            const notes = await listNotesByContactId(contactId);
            // ✅ CORRECCIÓN: Solo actualizar estado si el componente sigue montado
            if (isMountedRef.current) {
                setNotes(notes || []);
            }
        } catch (e) {
            console.error("Error cargando notas:", e);
            // ✅ CORRECCIÓN: Solo mostrar toast si el componente sigue montado
            if (isMountedRef.current) {
                toast.error(e?.response?.data?.message || e?.message || 'Error al cargar observaciones');
            }
        } finally {
            // ✅ CORRECCIÓN: Solo actualizar estado si el componente sigue montado
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    const renderNoteList = () => {
        if (notes.length === 0) {
            return null;
        }
        
        return notes.map((note) => {
            return (
                <ContactNotesDialogListItem
                    note={note}
                    key={note.id}
                    deleteItem={handleOpenDialogDelete}
                />
            );
        });
    };

    if (!contactId) {
        return null;
    }

    return (
        <>
            <ConfirmationModal
                title={i18n.t("contactModal.notes.deleteTitle") || "Eliminar Observación"}
                open={showOnDeleteDialog}
                onClose={() => setShowOnDeleteDialog(false)}
                onConfirm={handleDelete}
            >
                {i18n.t("contactModal.notes.deleteMessage") || "¿Desea realmente eliminar esta observación?"}
            </ConfirmationModal>
            
            <Box className={classes.root}>
                <Typography variant="subtitle1" className={classes.title}>
                    {i18n.t("contactModal.notes.title") || "Observaciones del Contacto"}
                </Typography>
                
                {/* ✅ NUEVO: Input sin Form anidado, usando div en lugar de Form */}
                <Box className={classes.inputContainer}>
                    <TextField
                        className={classes.textField}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={i18n.t("ticketOptionsMenu.appointmentsModal.placeholder") || "Escriba una observación..."}
                        multiline
                        rows={2}
                        variant="outlined"
                        margin="dense"
                        size="small"
                        disabled={loading}
                    />
                    <IconButton
                        className={classes.addButton}
                        color="primary"
                        onClick={handleSave}
                        disabled={loading || !noteText.trim()}
                        size="small"
                    >
                        {loading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <AddIcon />
                        )}
                    </IconButton>
                </Box>
                
                {notes.length > 0 && (
                    <List className={classes.list}>
                        {renderNoteList()}
                    </List>
                )}
            </Box>
        </>
    );
}

export default ContactNotesStandalone;
